import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import { X, Send, Loader2, Trash2, Edit2, User, Calendar, ExternalLink, Radar } from 'lucide-react';
import { useContacts } from '../hooks/useContacts';
import { useAuth } from '../hooks/useAuth';
import { formatCurrencyCompact } from '../utils/formatHelpers';
import AgentActionsPanel from './AgentActionsPanel';
import SegmentacaoEmpresa from './SegmentacaoEmpresa';
import SearchBuilder from './audiencias/SearchBuilder';

const HistoryModal = ({ empresa, onClose }) => {
    const { user } = useAuth();
    // Snapshot local da empresa para refletir edicoes de segmentacao imediatamente
    const [empresaLocal, setEmpresaLocal] = useState(empresa);
    useEffect(() => { setEmpresaLocal(empresa); }, [empresa?.id]);
    const [history, setHistory] = useState([]);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [showSearchBuilder, setShowSearchBuilder] = useState(false);

    // Estados do Formulario de Criacao
    const [newNote, setNewNote] = useState('');
    const [newDate, setNewDate] = useState(new Date().toISOString().split('T')[0]);
    const [selectedContactId, setSelectedContactId] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    // Estados de Edicao
    const [editingId, setEditingId] = useState(null);
    const [editNote, setEditNote] = useState('');
    const [editDate, setEditDate] = useState('');
    const [editContactId, setEditContactId] = useState(null);

    const { fetchHistory, addHistory, updateHistory, deleteHistory, contatos } = useContacts();

    // Filtra contatos da empresa usando useMemo (evita re-renders)
    // CORRIGIDO: exclui contatos virtuais (empresas sem contato real)
    const companyContacts = useMemo(() => {
        if (!empresa?.id || !contatos) return [];
        return contatos.filter(c => 
            c.empresa_id && 
            String(c.empresa_id).toLowerCase() === String(empresa.id).toLowerCase() &&
            !c._isVirtual
        );
    }, [empresa?.id, contatos]);

    // Carrega histórico apenas ao abrir o modal
    useEffect(() => {
        let isMounted = true;
        const loadHistory = async () => {
            if (empresa?.id) {
                setIsLoadingData(true);
                const data = await fetchHistory(empresa.id);
                if (isMounted) {
                    setHistory(data || []);
                    setIsLoadingData(false);
                }
            }
        };
        loadHistory();
        return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [empresa?.id]);

    // Opcoes para o Select
    const contactOptions = useMemo(() => {
        return companyContacts.map(c => ({
            value: c.id,
            label: `${c.first_name || ''} ${c.last_name || ''} ${c.title ? `(${c.title})` : ''}`.trim() || 'Sem Nome'
        }));
    }, [companyContacts]);

    const currentSelectValue = contactOptions.find(o => o.value === selectedContactId) || null;
    const currentEditSelectValue = contactOptions.find(o => o.value === editContactId) || null;

    const reloadHistory = async () => {
        const data = await fetchHistory(empresa.id);
        setHistory(data || []);
    };

    // --- Ações ---

    const handleSaveNew = async () => {
        if (!newNote.trim()) return;
        setIsSaving(true);
        const result = await addHistory(empresa.id, {
            texto: newNote,
            data_evento: newDate,
            contato_id: selectedContactId
        });
        if (result.success) {
            setNewNote('');
            setNewDate(new Date().toISOString().split('T')[0]);
            setSelectedContactId(null);
            await reloadHistory();
        } else {
            alert("Erro ao salvar.");
        }
        setIsSaving(false);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Tem certeza que deseja excluir esta interação?')) {
            await deleteHistory(id);
            await reloadHistory();
        }
    };

    const startEditing = (item) => {
        setEditingId(item.id);
        setEditNote(item.texto || '');
        setEditDate(item.data_evento || '');
        setEditContactId(item.contato_id);
    };

    const cancelEditing = () => {
        setEditingId(null);
        setEditNote('');
        setEditDate('');
        setEditContactId(null);
    };

    const saveEdit = async (id) => {
        await updateHistory(id, {
            texto: editNote,
            data_evento: editDate,
            contato_id: editContactId
        });
        cancelEditing();
        await reloadHistory();
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-[95vw] 2xl:max-w-7xl flex flex-col max-h-[95vh]">
                
                {/* Header */}
                <div className="flex justify-between items-center p-5 border-b bg-slate-50 rounded-t-xl">
                    <div>
                        <h2 className="text-xl font-bold text-slate-800">Histórico de Interações</h2>
                        <p className="text-sm text-slate-500 font-medium mt-1">{empresa?.empresa}</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-500">
                        <X size={24} />
                    </button>
                </div>

                {/* Perfil Apollo (só se a empresa foi enriquecida) */}
                {empresa?.apollo_id && (
                    <div className="px-5 py-4 border-b bg-emerald-50/40">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-bold text-emerald-800 flex items-center gap-2">
                                🟢 Perfil Apollo
                            </h3>
                            <a
                                href={`https://app.apollo.io/#/accounts/${empresa.apollo_id}/overview`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-emerald-700 hover:text-emerald-900 hover:underline flex items-center gap-1"
                            >
                                Abrir no Apollo <ExternalLink size={11} />
                            </a>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-700">
                            {empresa.apollo_industry && (
                                <div><strong className="text-slate-600">Indústria:</strong> {empresa.apollo_industry}</div>
                            )}
                            {empresa.apollo_num_employees != null && (
                                <div>
                                    <strong className="text-slate-600">Funcionários:</strong>{' '}
                                    {Number(empresa.apollo_num_employees).toLocaleString('pt-BR')}
                                </div>
                            )}
                            {empresa.apollo_revenue_usd != null && Number(empresa.apollo_revenue_usd) > 0 && (
                                <div>
                                    <strong className="text-slate-600">Receita (USD):</strong>{' '}
                                    {formatCurrencyCompact(empresa.apollo_revenue_usd)}
                                </div>
                            )}
                            {empresa.apollo_founded_year && (
                                <div><strong className="text-slate-600">Fundada em:</strong> {empresa.apollo_founded_year}</div>
                            )}
                            {(empresa.apollo_hq_city || empresa.apollo_hq_country) && (
                                <div className="col-span-2">
                                    <strong className="text-slate-600">Sede:</strong>{' '}
                                    {[empresa.apollo_hq_city, empresa.apollo_hq_country].filter(Boolean).join(', ')}
                                </div>
                            )}
                            {empresa.apollo_linkedin_url && (
                                <div className="col-span-2 truncate">
                                    <strong className="text-slate-600">LinkedIn:</strong>{' '}
                                    <a
                                        href={empresa.apollo_linkedin_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:underline"
                                    >
                                        {empresa.apollo_linkedin_url.replace(/^https?:\/\//, '')}
                                    </a>
                                </div>
                            )}
                        </div>
                        {empresa.apollo_keywords?.length > 0 && (
                            <div className="mt-2">
                                <div className="text-xs text-slate-600 font-semibold mb-1">Keywords</div>
                                <div className="flex flex-wrap gap-1">
                                    {empresa.apollo_keywords.slice(0, 10).map((kw, i) => (
                                        <span
                                            key={i}
                                            className="bg-emerald-100 text-emerald-800 text-[11px] px-2 py-0.5 rounded-full"
                                        >
                                            {kw}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                        {empresa.apollo_last_refresh_at && (
                            <div className="text-[11px] text-slate-500 mt-2">
                                Atualizado em {new Date(empresa.apollo_last_refresh_at).toLocaleDateString('pt-BR')}
                            </div>
                        )}
                    </div>
                )}

                {/* Frente C filtros_compactos 2026-04-26: segmentacao editavel */}
                {empresaLocal && (
                    <SegmentacaoEmpresa
                        empresa={empresaLocal}
                        userEmail={user?.email}
                        onUpdated={(updates) => setEmpresaLocal(prev => prev ? { ...prev, ...updates } : prev)}
                    />
                )}

                {/* Sprint 1: painel de agentes IA */}
                {empresa && (
                    <div className="px-5 py-4 border-b">
                        <AgentActionsPanel empresa={empresa} />
                        <button
                            onClick={() => setShowSearchBuilder(true)}
                            className="mt-3 px-3 py-1.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700 flex items-center gap-1.5 text-sm"
                            title="Abrir SearchBuilder Apollo com esta empresa pré-selecionada"
                        >
                            <Radar size={14} /> Buscar pessoas no Apollo
                        </button>
                    </div>
                )}

                {/* Timeline Body */}
                <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50 space-y-4">
                    {isLoadingData ? (
                        <div className="flex justify-center py-10">
                            <Loader2 className="animate-spin text-blue-500" />
                        </div>
                    ) : history.length === 0 ? (
                        <div className="text-center py-10 text-slate-400">
                            Nenhuma interação encontrada.
                        </div>
                    ) : (
                        history.map((item) => (
                            <div key={item.id} className="bg-white p-4 rounded-lg shadow-sm border border-slate-200 hover:shadow-md transition-all">
                                {editingId === item.id ? (
                                    // MODO EDICAO
                                    <div className="space-y-3">
                                        <div className="flex gap-3">
                                            <input 
                                                type="date" 
                                                value={editDate} 
                                                onChange={e => setEditDate(e.target.value)} 
                                                className="border rounded p-2 text-sm" 
                                            />
                                            <Select 
                                                options={contactOptions} 
                                                value={currentEditSelectValue}
                                                onChange={val => setEditContactId(val?.value || null)}
                                                className="flex-1 text-sm" 
                                                placeholder="Contato..."
                                                isClearable
                                                menuPortalTarget={document.body}
                                                styles={{ menuPortal: base => ({ ...base, zIndex: 10000 }) }}
                                            />
                                        </div>
                                        <textarea 
                                            value={editNote} 
                                            onChange={e => setEditNote(e.target.value)} 
                                            className="w-full border rounded p-2 text-sm min-h-[80px]" 
                                        />
                                        <div className="flex justify-end gap-2">
                                            <button 
                                                onClick={cancelEditing} 
                                                className="px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-100 rounded"
                                            >
                                                Cancelar
                                            </button>
                                            <button 
                                                onClick={() => saveEdit(item.id)} 
                                                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                                            >
                                                Salvar
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    // MODO VISUALIZACAO
                                    <>
                                        <div className="flex justify-between items-start mb-3 pb-2 border-b border-slate-100">
                                            <div className="flex items-center gap-3 flex-wrap">
                                                {item.contatos ? (
                                                    <span className="flex items-center gap-1 text-xs font-bold text-slate-700 bg-slate-100 px-2 py-1 rounded">
                                                        <User size={12}/> 
                                                        {item.contatos.first_name} {item.contatos.last_name}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-slate-400 italic">Geral</span>
                                                )}
                                                
                                                <div className="flex items-center gap-1 text-xs text-slate-500 font-medium">
                                                    <Calendar size={12} />
                                                    {item.data_evento 
                                                        ? new Date(item.data_evento).toLocaleDateString('pt-BR') 
                                                        : '-'}
                                                </div>

                                                <span className="text-[10px] text-slate-400 border px-1.5 py-0.5 rounded">
                                                    por {item.user_email?.split('@')[0] || 'sistema'}
                                                </span>
                                            </div>

                                            <div className="flex gap-1">
                                                <button 
                                                    onClick={() => startEditing(item)} 
                                                    className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded" 
                                                    title="Editar"
                                                >
                                                    <Edit2 size={14} />
                                                </button>
                                                <button 
                                                    onClick={() => handleDelete(item.id)} 
                                                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded" 
                                                    title="Excluir"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                        
                                        <p className="text-slate-700 whitespace-pre-wrap text-sm leading-relaxed">
                                            {item.texto}
                                        </p>
                                    </>
                                )}
                            </div>
                        ))
                    )}
                </div>

                {/* Footer - Nova Interação */}
                <div className="p-5 border-t bg-white rounded-b-xl space-y-3">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                        Nova Interação
                    </label>
                    
                    <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                            <label className="block text-xs text-slate-600 mb-1">Contato</label>
                            <Select
                                options={contactOptions}
                                value={currentSelectValue}
                                onChange={(val) => setSelectedContactId(val?.value || null)}
                                placeholder="Selecione..."
                                className="text-sm"
                                isClearable
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: base => ({ ...base, zIndex: 10000 }) }}
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-slate-600 mb-1">Data</label>
                            <input 
                                type="date" 
                                value={newDate} 
                                onChange={(e) => setNewDate(e.target.value)}
                                className="w-full border border-slate-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                        </div>
                    </div>
                    
                    <div className="flex gap-3">
                        <textarea
                            value={newNote}
                            onChange={(e) => setNewNote(e.target.value)}
                            placeholder="Descreva o que aconteceu..."
                            className="flex-1 border border-slate-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-y min-h-[80px]"
                        />
                        <button 
                            onClick={handleSaveNew}
                            disabled={!newNote.trim() || isSaving}
                            className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white px-5 rounded-lg font-medium flex items-center gap-2 transition-colors self-end h-12"
                        >
                            {isSaving ? (
                                <Loader2 size={18} className="animate-spin" />
                            ) : (
                                <Send size={18} />
                            )}
                            Salvar
                        </button>
                    </div>
                </div>
            </div>

            {showSearchBuilder && empresa && (
                <SearchBuilder
                    mode="execute"
                    empresaContext={{ id: empresa.id, company_name: empresa.company_name || empresa.empresa, apollo_id: empresa.apollo_id }}
                    onClose={() => setShowSearchBuilder(false)}
                    onSearchComplete={() => setShowSearchBuilder(false)}
                />
            )}
        </div>
    );
};

export default HistoryModal;
